import React, { Fragment } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Dispatch, AnyAction } from 'redux';
import { BaseCustomizationEntry, IAppState, WebchatOptions } from "../../Redux/reduxState";
import { genericSingleAction, actionTypes } from "../../Redux/actions";
import { UISelectorType } from "../../utilities/types";
import { DefaultSelector } from "./defaultSelector";
import { BooleanSelector } from "./booleanSelector";
import { IntegerSelector } from "./integerSelector";
import { PercentageSelector } from "./percentageSelector";
import { ColorSelector } from "./colorSelector";
import { RgbaSelector } from './rgbaSelector'

import { mergeStyles } from "@fluentui/react";
import { DropDownSelector } from "./dropDownSelector";
import { ContainerName } from "../../Redux/reducers/AppReducer";
import { DEFAULT_UNDEFINED_RGBA } from "../constants/defaultStyleOptions";
import { ReactSelector } from "./reactSelector";
import { LocalizationSelector } from "./localizationSelector";
import WebChatOptionsSearch from "./WebChatOptionsSearch";
import ThemeSelector from "./themeSelector";


const UI_selector = mergeStyles(
    {
        fontSize: '15px',
        fontWeight: '400',
        paddingBottom: 15
    }
);

const highlightedLabel = mergeStyles(
    {
        color: 'var(--druid-color-success-primary)',
        transition: 'color var(--druid-transition-base) ease',
    }
);

const disabledEntry = mergeStyles(
    {
        opacity: 0.5,
        pointerEvents: 'none',
    }
);

interface CustomizationEntrySelectorProps {
    entry: BaseCustomizationEntry;
    value: any;
    onChange: (styleElementName: string, value: any, containerName?: ContainerName) => void;
    getStyleOptionValue: (key: any, containerName?: ContainerName) => any;
    highlighted?: boolean;
}

export const CustomizationEntrySelector = (props: CustomizationEntrySelectorProps) => {
    const { entry, value, onChange, highlighted } = props;
    const { displayName, id } = entry;

    const getHiddenDependencyValue = (dependencyPath: string) => {
        const trimmedDependencyPath = dependencyPath.trim();
        if (!trimmedDependencyPath) {
            return true;
        }

        if (trimmedDependencyPath.includes('.')) {
            const pathParts = trimmedDependencyPath.split('.');
            const key = pathParts.pop() as string;
            const containerName = pathParts.join('.') as ContainerName;
            return props.getStyleOptionValue(key, containerName);
        }

        const valueFromCurrentContainer = props.getStyleOptionValue(trimmedDependencyPath, entry.containerName);
        if (valueFromCurrentContainer !== undefined && valueFromCurrentContainer !== null) {
            return valueFromCurrentContainer;
        }

        const fallbackContainers: ContainerName[] = ['UI.headerOptions', 'UI', 'styleOptions', 'root'];
        for (const fallbackContainer of fallbackContainers) {
            const fallbackValue = props.getStyleOptionValue(trimmedDependencyPath, fallbackContainer);
            if (fallbackValue !== undefined && fallbackValue !== null) {
                return fallbackValue;
            }
        }

        return undefined;
    }

    const isEntryHidden = () => {
        if (entry.hidden === true) {
            return true;
        }

        if (!Array.isArray(entry.hidden) || entry.hidden.length === 0) {
            return false;
        }

        return entry.hidden.some((dependencyPath) => {
            const isNegatedDependency = dependencyPath.startsWith('!');
            const normalizedDependencyPath = isNegatedDependency ? dependencyPath.slice(1) : dependencyPath;
            const dependencyValue = Boolean(getHiddenDependencyValue(normalizedDependencyPath));
            return isNegatedDependency ? dependencyValue : !dependencyValue;
        });
    }

    if (isEntryHidden()) {
        return null;
    }

    const isEntryDisabled = () => {
        if (entry.disabled === true) {
            return true;
        }

        if (!Array.isArray(entry.disabled) || entry.disabled.length === 0) {
            return false;
        }

        return entry.disabled.some((dependencyPath) => {
            const isNegatedDependency = dependencyPath.startsWith('!');
            const normalizedDependencyPath = isNegatedDependency ? dependencyPath.slice(1) : dependencyPath;
            const dependencyValue = Boolean(getHiddenDependencyValue(normalizedDependencyPath));
            return isNegatedDependency ? !dependencyValue : dependencyValue;
        });
    }

    const entryIsDisabled = isEntryDisabled();

    const onChangeWithContainerName = (styleElementName: string, value: any) => {
        props.onChange(styleElementName, value, entry.containerName);
    }

    const renderSelector = (type: UISelectorType) => {
        switch (type) {
            case UISelectorType.booleanSelector:
                return <BooleanSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.integerSelector:
                return <IntegerSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.percentageSelector:
                return <PercentageSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.colorSelector:
                return <ColorSelector id={id} onChange={onChangeWithContainerName} value={value} getStyleOptionValue={props.getStyleOptionValue} />
            case UISelectorType.dropDownSelector:
                return <DropDownSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.rgbaSelector:
                return <RgbaSelector id={id} onChange={onChangeWithContainerName} value={value || DEFAULT_UNDEFINED_RGBA} />
            case UISelectorType.reactSelector:
                return <ReactSelector id={id} onChange={onChangeWithContainerName} value={value || ""} defaultValue={entry.defaultValue} />
            case UISelectorType.localizationSelector:
                return <LocalizationSelector
                    id={id} onChange={onChangeWithContainerName}
                    value={value || ""} defaultValue={entry.defaultValue}
                    languages={props.getStyleOptionValue("languages", "root")}
                    isVoice={entry.containerName === "VoiceUI"}
                    />
            case UISelectorType.searchSelector:
                return <WebChatOptionsSearch />
            case UISelectorType.themeSelector:
                return <ThemeSelector id={id} value={value} onChange={onChangeWithContainerName} />
            case UISelectorType.defaultSelector:
            default:
                return <DefaultSelector multiline={entry.multiline} id={id} onChange={onChangeWithContainerName} value={value} defaultValue={entry.defaultValue}/>
        }
    }

    const isBespokePanel = entry.uiSelectorType === UISelectorType.searchSelector || entry.uiSelectorType === UISelectorType.themeSelector;

    return (
        <div className={`${UI_selector}${entryIsDisabled ? ` ${disabledEntry}` : ''}`} data-entry-id={id} aria-disabled={entryIsDisabled || undefined}>
            <span className={highlighted ? highlightedLabel : undefined}>
                {!isBespokePanel && displayName} {!isBespokePanel && <strong> ({id}) </strong>}
            </span>

            {renderSelector(entry.uiSelectorType)}
        </div>
    );
}
